import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Loader2,
  Building,
  PlusCircle,
  Edit,
  Trash2,
  Mail,
  Phone,
  DollarSign,
  FileText,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

const VENDOR_TYPES = [
  'Installer',
  'Equipment Supplier',
  'Financing Partner',
  'Permit Service',
  'Design Service',
  'Marketing Partner',
  'Other',
];

const Vendors = () => {
  const { isAdmin } = useAuth();
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingVendor, setEditingVendor] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    vendor_type: 'Installer',
    description: '',
    contact_name: '',
    contact_email: '',
    contact_phone: '',
    payout_instructions: '',
    payout_structure: '',
    payout_per_kw: '',
    payout_flat_fee: '',
    is_active: true,
  });

  useEffect(() => {
    loadVendors();
  }, []);

  const loadVendors = async () => {
    try {
      const response = await api.get('/vendors');
      setVendors(response.data);
    } catch (error) {
      toast.error('Failed to load vendors');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      vendor_type: 'Installer',
      description: '',
      contact_name: '',
      contact_email: '',
      contact_phone: '',
      payout_instructions: '',
      payout_structure: '',
      payout_per_kw: '',
      payout_flat_fee: '',
      is_active: true,
    });
    setEditingVendor(null);
  };

  const openEditDialog = (vendor) => {
    setEditingVendor(vendor);
    setFormData({
      name: vendor.name || '',
      vendor_type: vendor.vendor_type || 'Installer',
      description: vendor.description || '',
      contact_name: vendor.contact_name || '',
      contact_email: vendor.contact_email || '',
      contact_phone: vendor.contact_phone || '',
      payout_instructions: vendor.payout_instructions || '',
      payout_structure: vendor.payout_structure || '',
      payout_per_kw: vendor.payout_per_kw || '',
      payout_flat_fee: vendor.payout_flat_fee || '',
      is_active: vendor.is_active !== false,
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      toast.error('Please enter a vendor name');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...formData,
        payout_per_kw: formData.payout_per_kw ? parseFloat(formData.payout_per_kw) : null,
        payout_flat_fee: formData.payout_flat_fee ? parseFloat(formData.payout_flat_fee) : null,
      };

      if (editingVendor) {
        await api.put(`/vendors/${editingVendor.id}`, payload);
        toast.success('Vendor updated successfully');
      } else {
        await api.post('/vendors', payload);
        toast.success('Vendor created successfully');
      }
      
      loadVendors();
      setDialogOpen(false);
      resetForm();
    } catch (error) {
      toast.error('Failed to save vendor');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (vendorId) => {
    if (!window.confirm('Are you sure you want to delete this vendor?')) return;
    
    try {
      await api.delete(`/vendors/${vendorId}`);
      toast.success('Vendor deleted');
      loadVendors();
    } catch (error) {
      toast.error('Failed to delete vendor');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="vendors-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-heading font-bold text-slate-900 dark:text-white">
            Vendor Management
          </h1>
          <p className="text-slate-600 dark:text-slate-400 mt-1">
            Manage vendors, their services, and payout structures
          </p>
        </div>
        {isAdmin() && (
          <Dialog open={dialogOpen} onOpenChange={(open) => {
            setDialogOpen(open);
            if (!open) resetForm();
          }}>
            <DialogTrigger asChild>
              <Button className="bg-emerald-600 hover:bg-emerald-700 text-white shadow-lg shadow-emerald-600/20" data-testid="add-vendor-btn">
                <PlusCircle className="w-4 h-4 mr-2" />
                Add Vendor
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Building className="w-5 h-5 text-emerald-600" />
                  {editingVendor ? 'Edit Vendor' : 'Add New Vendor'}
                </DialogTitle>
                <DialogDescription>
                  {editingVendor ? 'Update vendor information and payout structure' : 'Add a new vendor partner with their payout details'}
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit}>
                <div className="space-y-4 py-4">
                  {/* Basic Info */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="name">Vendor Name *</Label>
                      <Input
                        id="name"
                        value={formData.name}
                        onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                        placeholder="ABC Solar Installers"
                        required
                        data-testid="vendor-name-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="vendor_type">Vendor Type</Label>
                      <Select
                        value={formData.vendor_type}
                        onValueChange={(value) => setFormData(prev => ({ ...prev, vendor_type: value }))}
                      >
                        <SelectTrigger data-testid="vendor-type-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {VENDOR_TYPES.map((type) => (
                            <SelectItem key={type} value={type}>{type}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="description">Description</Label>
                    <Textarea
                      id="description"
                      value={formData.description}
                      onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                      placeholder="What services does this vendor provide?"
                      rows={2}
                      data-testid="vendor-description-input"
                    />
                  </div>

                  {/* Contact Info */}
                  <div className="border-t pt-4">
                    <h4 className="font-medium mb-3">Contact Information</h4>
                    <div className="grid grid-cols-3 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="contact_name">Contact Name</Label>
                        <Input
                          id="contact_name"
                          value={formData.contact_name}
                          onChange={(e) => setFormData(prev => ({ ...prev, contact_name: e.target.value }))}
                          placeholder="John Smith"
                          data-testid="vendor-contact-name-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="contact_email">Email</Label>
                        <Input
                          id="contact_email"
                          type="email"
                          value={formData.contact_email}
                          onChange={(e) => setFormData(prev => ({ ...prev, contact_email: e.target.value }))}
                          placeholder="john@vendor.com"
                          data-testid="vendor-email-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="contact_phone">Phone</Label>
                        <Input
                          id="contact_phone"
                          value={formData.contact_phone}
                          onChange={(e) => setFormData(prev => ({ ...prev, contact_phone: e.target.value }))}
                          placeholder="(555) 123-4567"
                          data-testid="vendor-phone-input"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Payout Info */}
                  <div className="border-t pt-4">
                    <h4 className="font-medium mb-3">Payout Structure</h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="payout_per_kw">Payout per kW ($)</Label>
                        <Input
                          id="payout_per_kw"
                          type="number"
                          step="0.01"
                          min="0"
                          value={formData.payout_per_kw}
                          onChange={(e) => setFormData(prev => ({ ...prev, payout_per_kw: e.target.value }))}
                          placeholder="0.00"
                          data-testid="vendor-payout-kw-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="payout_flat_fee">Flat Fee ($)</Label>
                        <Input
                          id="payout_flat_fee"
                          type="number"
                          step="0.01"
                          min="0"
                          value={formData.payout_flat_fee}
                          onChange={(e) => setFormData(prev => ({ ...prev, payout_flat_fee: e.target.value }))}
                          placeholder="0.00"
                          data-testid="vendor-payout-flat-input"
                        />
                      </div>
                    </div>
                    <div className="space-y-2 mt-4">
                      <Label htmlFor="payout_structure">Payout Structure Description</Label>
                      <Textarea
                        id="payout_structure"
                        value={formData.payout_structure}
                        onChange={(e) => setFormData(prev => ({ ...prev, payout_structure: e.target.value }))}
                        placeholder="Describe how this vendor gets paid (e.g., 'Net 30 after installation completion')"
                        rows={2}
                        data-testid="vendor-payout-structure-input"
                      />
                    </div>
                    <div className="space-y-2 mt-4">
                      <Label htmlFor="payout_instructions">Payout Instructions</Label>
                      <Textarea
                        id="payout_instructions"
                        value={formData.payout_instructions}
                        onChange={(e) => setFormData(prev => ({ ...prev, payout_instructions: e.target.value }))}
                        placeholder="Special instructions for processing payments"
                        rows={2}
                        data-testid="vendor-payout-instructions-input"
                      />
                    </div>
                  </div>

                  {/* Active Status */}
                  <div className="flex items-center justify-between border-t pt-4">
                    <div>
                      <Label>Active Status</Label>
                      <p className="text-sm text-slate-500">Inactive vendors won't appear in lead forms</p>
                    </div>
                    <Switch
                      checked={formData.is_active}
                      onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_active: checked }))}
                      data-testid="vendor-active-switch"
                    />
                  </div>
                </div>
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    disabled={saving}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white"
                    data-testid="save-vendor-btn"
                  >
                    {saving ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      editingVendor ? 'Update Vendor' : 'Add Vendor'
                    )}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Vendors Table */}
      <Card>
        <CardHeader>
          <CardTitle className="font-heading">Vendor Partners</CardTitle>
          <CardDescription>
            {vendors.length} vendor{vendors.length !== 1 ? 's' : ''} registered
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          {vendors.length > 0 ? (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50 dark:bg-slate-800/50">
                    <TableHead>Vendor</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Contact</TableHead>
                    <TableHead>Payout Structure</TableHead>
                    <TableHead>Status</TableHead>
                    {isAdmin() && <TableHead className="text-right">Actions</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {vendors.map((vendor) => (
                    <TableRow key={vendor.id} data-testid={`vendor-row-${vendor.id}`}>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center">
                            <Building className="w-5 h-5 text-blue-600" />
                          </div>
                          <div>
                            <p className="font-medium text-slate-900 dark:text-white">
                              {vendor.name}
                            </p>
                            {vendor.description && (
                              <p className="text-sm text-slate-500 truncate max-w-[200px]">
                                {vendor.description}
                              </p>
                            )}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">{vendor.vendor_type}</Badge>
                      </TableCell>
                      <TableCell>
                        <div className="space-y-1">
                          {vendor.contact_name && (
                            <p className="text-sm">{vendor.contact_name}</p>
                          )}
                          {vendor.contact_email && (
                            <a href={`mailto:${vendor.contact_email}`} className="flex items-center gap-1 text-xs text-slate-500 hover:text-emerald-600">
                              <Mail className="w-3 h-3" />
                              {vendor.contact_email}
                            </a>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        {isAdmin() ? (
                          <div className="space-y-1">
                            {vendor.payout_per_kw && (
                              <p className="text-sm font-mono">
                                <DollarSign className="w-3 h-3 inline" />
                                {vendor.payout_per_kw}/kW
                              </p>
                            )}
                            {vendor.payout_flat_fee && (
                              <p className="text-sm font-mono">
                                <DollarSign className="w-3 h-3 inline" />
                                {vendor.payout_flat_fee} flat
                              </p>
                            )}
                            {!vendor.payout_per_kw && !vendor.payout_flat_fee && (
                              <span className="text-slate-400">—</span>
                            )}
                          </div>
                        ) : (
                          <span className="text-slate-400 text-sm">Admin Only</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge className={vendor.is_active 
                          ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400' 
                          : 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400'
                        }>
                          {vendor.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                      </TableCell>
                      {isAdmin() && (
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openEditDialog(vendor)}
                              data-testid={`edit-vendor-${vendor.id}`}
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDelete(vendor.id)}
                              className="text-red-600 hover:text-red-700"
                              data-testid={`delete-vendor-${vendor.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="text-center py-16">
              <Building className="w-12 h-12 mx-auto mb-4 text-slate-300 dark:text-slate-600" />
              <h3 className="text-lg font-medium text-slate-900 dark:text-white mb-2">
                No vendors yet
              </h3>
              <p className="text-slate-500 dark:text-slate-400 mb-6">
                Add your first vendor partner to get started
              </p>
              {isAdmin() && (
                <Button
                  onClick={() => setDialogOpen(true)}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                >
                  <PlusCircle className="w-4 h-4 mr-2" />
                  Add First Vendor
                </Button>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Vendors;
